import React, { useState, useRef } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Grid, Typography, Box, Card, CardContent, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Chip, LinearProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  School as SchoolIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

import { usePermissions } from '../../../utils/permissions';

export default function CertificationsTab({ 
  certifications, 
  onCertificationsChange, 
  companyId,
  employeeId 
}) {
  const { canEditCertifications, canDeleteCertifications } = usePermissions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentDeleteDialogOpen, setDocumentDeleteDialogOpen] = useState(false);
  const [certToDelete, setCertToDelete] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    certification_name: '',
    issuing_organization: '',
    earned_date: '',
    expiration_date: '',
    certificate_document_path: '',
    certificate_document_name: '',
    notes: ''
  });

  const handleAddCertification = () => {
    setEditingCert(null);
    setFormData({
      certification_name: '',
      issuing_organization: '',
      earned_date: '',
      expiration_date: '',
      certificate_document_path: '',
      certificate_document_name: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleEditCertification = (cert) => {
    setEditingCert(cert);
    setFormData({
      certification_name: cert.certification_name || '',
      issuing_organization: cert.issuing_organization || '',
      earned_date: cert.earned_date || '',
      expiration_date: cert.expiration_date || '',
      certificate_document_path: cert.certificate_document_path || '',
      certificate_document_name: cert.certificate_document_name || '',
      notes: cert.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteCertification = (cert) => {
    if (!canDeleteCertifications()) {
      setError('You do not have permission to delete certifications');
      return;
    }
    
    setCertToDelete(cert);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCertification = async () => {
    if (!certToDelete) return;
    
    try {
      // Delete from database if it has a real ID (not temporary)
      if (certToDelete.id && !certToDelete.id.toString().startsWith('temp_')) {
        const { error: dbError } = await supabase
          .from('employee_certifications')
          .delete()
          .eq('id', certToDelete.id);
        
        if (dbError) throw dbError;
      }

      // Delete certificate document from storage if it exists
      if (certToDelete.certificate_document_path) {
        const { error: deleteFileError } = await supabase.storage
          .from('employee-documents')
          .remove([certToDelete.certificate_document_path]);
        
        if (deleteFileError) {
          console.warn('Error deleting certificate document:', deleteFileError);
        }
      }

      // Remove from local state
      const updatedCerts = certifications.filter(cert => cert.id !== certToDelete.id);
      onCertificationsChange(updatedCerts);
      
      setDeleteDialogOpen(false);
      setCertToDelete(null);
    } catch (error) {
      console.error('Error deleting certification:', error);
      setError('Error deleting certification: ' + error.message);
    }
  };

  const handleSaveCertification = async () => {
    if (!formData.certification_name) {
      setError('Certification name is required');
      return;
    }

    try {
      const certData = {
        certification_name: formData.certification_name.trim(),
        issuing_organization: formData.issuing_organization?.trim() || null,
        earned_date: formData.earned_date || null,
        expiration_date: formData.expiration_date || null,
        certificate_document_path: formData.certificate_document_path?.trim() || null,
        certificate_document_name: formData.certificate_document_name?.trim() || null,
        notes: formData.notes?.trim() || null,
        is_active: true
      };

      if (editingCert && editingCert.id) {
        // Update existing certification in local state
        const updatedCerts = certifications.map(cert => 
          cert.id === editingCert.id ? { ...cert, ...certData } : cert
        );
        onCertificationsChange(updatedCerts);
      } else {
        // Add new certification to local state
        const newCert = {
          ...certData,
          id: `temp_${Date.now()}`
        };
        onCertificationsChange([...certifications, newCert]);
      }
      
      setDialogOpen(false);
      setError('');
    } catch (error) {
      console.error('Error saving certification:', error);
      setError('Error saving certification: ' + error.message);
    }
  };

  const loadCertifications = async () => {
    if (!employeeId) return;
    
    try {
      const { data, error } = await supabase
        .rpc('get_employee_certifications_decrypted', {
          employee_id_param: employeeId,
          company_id_param: companyId
        });

      if (error) throw error;
      onCertificationsChange(data || []);
    } catch (error) {
      console.error('Error loading certifications:', error);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    // Store old file path for deletion after successful upload
    const oldFilePath = formData.certificate_document_path;

    try {
      // Generate unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${companyId}/certifications/${fileName}`;

      // Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('employee-documents')
        .upload(filePath, file, {
          onUploadProgress: (progress) => {
            setUploadProgress((progress.loaded / progress.total) * 100);
          }
        });

      if (uploadError) throw uploadError;

      // Update form data
      setFormData(prev => ({
        ...prev,
        certificate_document_path: filePath,
        certificate_document_name: file.name
      }));

      // Delete old file after successful upload and form update
      if (oldFilePath && oldFilePath !== filePath) {
        try {
          const { error: deleteError } = await supabase.storage
            .from('employee-documents')
            .remove([oldFilePath]);
          
          if (deleteError) {
            console.warn('Error deleting old certificate:', deleteError);
          }
        } catch (deleteErr) {
          console.warn('Failed to delete old file:', deleteErr);
        }
      }

    } catch (error) {
      console.error('Error uploading certificate:', error);
      setError('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async () => {
    if (!formData.certificate_document_path) return;
    
    if (!canDeleteCertifications()) {
      setError('You do not have permission to delete certificate documents');
      return;
    }
    
    setDocumentDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    try {
      // Delete from storage
      const { error } = await supabase.storage
        .from('employee-documents')
        .remove([formData.certificate_document_path]);

      if (error) throw error;

      // Clear form data
      setFormData(prev => ({
        ...prev,
        certificate_document_path: '',
        certificate_document_name: ''
      }));

    } catch (error) {
      console.error('Error deleting certificate:', error);
      setError('Error deleting file: ' + error.message);
    } finally {
      setDocumentDeleteDialogOpen(false);
    }
  };

  const handleDownloadDocument = async (filePath, fileName) => {
    if (!filePath) return;

    try {
      const { data, error } = await supabase.storage
        .from('employee-documents')
        .download(filePath);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'certificate.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading certificate:', error);
      setError('Error downloading file: ' + error.message);
    }
  };

  const handleViewDocument = async (filePath) => {
    if (!filePath) return;

    try {
      const { data, error } = await supabase.storage
        .from('employee-documents')
        .createSignedUrl(filePath, 3600);

      if (error) throw error;

      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (error) {
      console.error('Error viewing certificate:', error);
      setError('Error viewing file: ' + error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
    return expDate <= thirtyDaysFromNow && expDate >= now;
  };

  return (
    <Box sx={{ p: 4 }}>
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
                border: '1px solid rgba(25, 118, 210, 0.2)'
              }}>
                <SchoolIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                Professional Certifications
              </Typography>
            </Box>
            {canEditCertifications() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddCertification}
                sx={{ fontWeight: 500 }}
              >
                Add Certification
              </Button>
            )}
          </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {certifications.length === 0 ? (
              <Box textAlign="center" py={4}>
                <SchoolIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  No Certifications Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add certifications to track professional credentials
                </Typography>
              </Box>
            ) : (
              <Paper sx={{ 
                borderRadius: 2,
                border: `1px solid rgba(0, 0, 0, 0.08)`,
                overflow: 'hidden'
              }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid rgba(25, 118, 210, 1)`,
                          color: 'primary.main',
                          py: 2
                        }}>Certification</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid rgba(25, 118, 210, 1)`,
                          color: 'primary.main',
                          py: 2
                        }}>Organization</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid rgba(25, 118, 210, 1)`,
                          color: 'primary.main',
                          py: 2
                        }}>Earned Date</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid rgba(25, 118, 210, 1)`,
                          color: 'primary.main',
                          py: 2
                        }}>Expiration</TableCell>
                        <TableCell sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid rgba(25, 118, 210, 1)`,
                          color: 'primary.main',
                          py: 2
                        }}>Status</TableCell>
                        <TableCell align="right" sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `2px solid rgba(25, 118, 210, 1)`,
                          color: 'primary.main',
                          py: 2
                        }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                  <TableBody>
                    {certifications.filter(cert => cert.is_active !== false).map((cert, index) => (
                      <TableRow key={cert.id || index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {cert.certification_name}
                          </Typography>
                          {cert.notes && (
                            <Typography variant="caption" color="text.secondary">
                              {cert.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {cert.issuing_organization}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(cert.earned_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(cert.expiration_date) || 'No expiration'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {cert.expiration_date && isExpired(cert.expiration_date) ? (
                            <Chip label="Expired" color="error" size="small" />
                          ) : cert.expiration_date && isExpiringSoon(cert.expiration_date) ? (
                            <Chip label="Expiring Soon" color="warning" size="small" />
                          ) : (
                            <Chip label="Valid" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={0.5} justifyContent="flex-end">
                            {cert.certificate_document_path && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDocument(cert.certificate_document_path)}
                                  title="View Certificate"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadDocument(
                                    cert.certificate_document_path,
                                    cert.certificate_document_name
                                  )}
                                  title="Download Certificate"
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </>
                            )}
                            {canEditCertifications() && (
                              <IconButton
                                size="small"
                                onClick={() => handleEditCertification(cert)}
                                title="Edit Certification"
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            {canDeleteCertifications() && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteCertification(cert)}
                                title="Delete Certification"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              </Paper>
            )}
          </CardContent>
        </Card>

      {/* Add/Edit Certification Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2)'
          }
        }}
      >
        <DialogTitle>
          {editingCert ? 'Edit Certification' : 'Add Certification'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Certification Name *"
                value={formData.certification_name}
                onChange={(e) => setFormData(prev => ({ ...prev, certification_name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Issuing Organization"
                value={formData.issuing_organization}
                onChange={(e) => setFormData(prev => ({ ...prev, issuing_organization: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Earned Date"
                  value={formData.earned_date ? dayjs(formData.earned_date) : null}
                  onChange={(newValue) => {
                    setFormData(prev => ({ ...prev, earned_date: newValue ? newValue.format('YYYY-MM-DD') : '' }));
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiration Date"
                  value={formData.expiration_date ? dayjs(formData.expiration_date) : null}
                  onChange={(newValue) => {
                    setFormData(prev => ({ ...prev, expiration_date: newValue ? newValue.format('YYYY-MM-DD') : '' }));
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: "Leave empty if certification doesn't expire",
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                multiline
                rows={3}
                placeholder="Additional notes about this certification"
              />
            </Grid>
            
            {/* Certificate Document Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                Certificate Document
              </Typography>
              
              {!formData.certificate_document_path ? (
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleFileSelect}
                    disabled={uploading}
                    fullWidth
                  >
                    {uploading ? 'Uploading...' : 'Upload Certificate'}
                  </Button>
                  
                  {uploading && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(uploadProgress)}% uploaded
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'between'
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formData.certificate_document_name}
                    </Typography>
                    <Chip
                      label={formData.certificate_document_name?.split('.').pop()?.toUpperCase() || 'FILE'}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Box>
                    <IconButton
                      onClick={() => handleViewDocument(formData.certificate_document_path)}
                      title="View"
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDownloadDocument(
                        formData.certificate_document_path,
                        formData.certificate_document_name
                      )}
                      title="Download"
                      size="small"
                    >
                      <DownloadIcon />
                    </IconButton>
                    {canDeleteCertifications() && (
                      <IconButton
                        onClick={handleDeleteDocument}
                        title="Delete"
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCertification}
            disabled={!formData.certification_name.trim()}
            sx={{ fontWeight: 500 }}
          >
            {editingCert ? 'Update' : 'Add'} Certification
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Certification Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        PaperProps={{
          sx: {
            position: 'fixed !important',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete Certification
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{certToDelete?.certification_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteCertification}
            color="error"
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Document Dialog */}
      <Dialog
        open={documentDeleteDialogOpen}
        onClose={() => setDocumentDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        disablePortal={false}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        PaperProps={{
          sx: {
            position: 'fixed !important',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            Delete Certificate Document
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{formData.certificate_document_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteDocument}
            color="error"
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}